import axios from "axios";
import { API_BASE_URL } from "./api";
import { getToken } from "./auth";

/**
 * Update call status and launch phone dialer
 * Reuses the exact same implementation as the Lead List/Client List call buttons
 * @param {string} id - Lead ID or Client ID
 * @param {string} phoneNumber - Phone number to dial
 * @param {boolean} isClient - Whether this is a client (true) or lead (false)
 * @param {function} showToast - Toast notification callback
 * @returns {Promise<boolean>} - Success/failure
 */
export const handleNotificationCall = async (
  id,
  phoneNumber,
  isClient = false,
  showToast = null
) => {
  // Validate inputs
  if (!id) {
    if (showToast) {
      showToast("Unable to identify the contact", "error");
    }
    return false;
  }

  if (!phoneNumber) {
    if (showToast) {
      showToast("Phone number not available for this contact", "error");
    }
    return false;
  }

  try {
    const token = getToken();
    const endpoint = isClient ? "clients" : "leads";
    
    // Make the API call to update call status to "waiting"
    // This uses the exact same endpoint and payload structure as the Lead/Client List call button
    const response = await axios.put(
      `${API_BASE_URL}/${endpoint}/call-status`,
      {
        [isClient ? "clientId" : "leadId"]: id,
        call_status: "waiting",
      },
      {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "application/json",
        },
      }
    );

    // If API call succeeds, open the phone dialer
    if (response?.status === 200 || response?.data?.status !== false) {
      launchDialer(phoneNumber);
      return true;
    } else {
      if (showToast) {
        const errorMessage = response?.data?.message || "Failed to update call status";
        showToast(errorMessage, "error");
      }
      return false;
    }
  } catch (error) {
    console.error("Error updating call status:", error);
    if (showToast) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to process call";
      showToast(errorMessage, "error");
    }
    return false;
  }
};

/**
 * Open the device phone dialer with the given number
 * Uses the tel: protocol for universal device compatibility
 * @param {string} phone - Phone number to dial
 */
const launchDialer = (phone) => {
  if (!phone) return;
  const cleanPhone = String(phone).trim();
  window.location.href = `tel:${cleanPhone}`;
};
